import { useEffect, useMemo, useState } from "react";

export default function JournalModal({ editing, accounts, onClose }) {
  const [voucher, setVoucher] = useState("");
  const [date, setDate] = useState("");
  const [reference, setReference] = useState("");
  const [narration, setNarration] = useState("");

  const [rows, setRows] = useState([
    {
      account_id: "",
      debit: "",
      credit: "",
      remark: "",
    },
  ]);

  useEffect(() => {
    if (editing) {
      setVoucher(editing.entry_no);
      setDate(editing.entry_date);
      setReference(editing.reference || "");
      setNarration(editing.narration || "");

      fetch(`/api/journal/${editing.id}`, {
        credentials: "include",
      })
        .then((r) => r.json())
        .then((data) => {
          setRows(data.items || []);
        });
    } else {
      setDate(new Date().toISOString().substring(0, 10));

      fetch("/api/journal/next-number", {
        credentials: "include",
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.status === "success") {
            setVoucher(d.entry_no);
          }
        });
    }
  }, [editing]);

  const addRow = () => {
    setRows([
      ...rows,
      {
        account_id: "",
        debit: "",
        credit: "",
        remark: "",
      },
    ]);
  };

  const removeRow = (index) => {
    if (rows.length <= 2) {
      return alert("Journal must contain at least two rows.");
    }
    const temp = [...rows];
    temp.splice(index, 1);
    setRows(temp);
  };

  const updateRow = (index, field, value) => {
    const temp = [...rows];
    temp[index][field] = value;
    setRows(temp);
  };

  const totalDebit = useMemo(() => {
    return rows.reduce((a, b) => a + Number(b.debit || 0), 0);
  }, [rows]);

  const totalCredit = useMemo(() => {
    return rows.reduce((a, b) => a + Number(b.credit || 0), 0);
  }, [rows]);

  const saveJournal = async () => {
    for (const row of rows) {
      if (!row.account_id) {
        return alert("Please select an account.");
      }

      const debit = Number(row.debit || 0);
      const credit = Number(row.credit || 0);

      if (debit === 0 && credit === 0) {
        return alert("Each row must have either debit or credit.");
      }

      if (debit > 0 && credit > 0) {
        return alert("A row cannot have both debit and credit.");
      }
    }

    const payload = {
      entry_no: voucher,
      entry_date: date,
      reference,
      narration,
      items: rows,
    };

    console.log(payload);

    const url = editing
      ? `/api/journal/${editing.id}`
      : "/api/journal";

    const method = editing ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.status === "success") {
      onClose();
    } else {
      alert(data.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="w-full max-w-6xl rounded-2xl bg-[#09090B] border border-white/10">
        <div className="flex justify-between items-center px-8 py-5 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-serif">
              {editing ? "Edit Journal" : "New Journal Entry"}
            </h2>

            <p className="text-xs text-gray-400">Double Entry Accounting</p>
          </div>

          <button onClick={onClose} className="text-xl">
            ✕
          </button>
        </div>

        <div className="p-8 space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-gray-400">Voucher</label>

              <input
                disabled
                value={voucher}
                className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              />
            </div>

            <div>
              <label className="text-xs text-gray-400">Date</label>

              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              />
            </div>

            <div>
              <label className="text-xs text-gray-400">Reference</label>

              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              />
            </div>

            <div>
              <label className="text-xs text-gray-400">Narration</label>

              <input
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              />
            </div>
          </div>

          <table className="w-full">
            <thead>
              <tr className="text-gray-400">
                <th className="text-left">Account</th>

                <th>Debit</th>

                <th>Credit</th>

                <th>Remark</th>

                <th></th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={row.account_id}
                      onChange={(e) =>
                        updateRow(index, "account_id", e.target.value)
                      }
                      className="w-full mt-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2"
                    >
                      <option value="">Select Account</option>

                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <input
                      type="number"
                      disabled={Number(row.credit) > 0}
                      value={row.debit}
                      onChange={(e) =>
                        updateRow(index, "debit", e.target.value)
                      }
                      className="w-full mt-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      disabled={Number(row.debit) > 0}
                      value={row.credit}
                      onChange={(e) =>
                        updateRow(index, "credit", e.target.value)
                      }
                      className="w-full mt-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2"
                    />
                  </td>

                  <td>
                    <input
                      value={row.remark}
                      onChange={(e) =>
                        updateRow(index, "remark", e.target.value)
                      }
                      className="w-full mt-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2"
                    />
                  </td>

                  <td>
                    <button
                      onClick={() => removeRow(index)}
                      className="text-red-400"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={addRow} className="px-5 py-2 rounded-xl bg-white/10">
            + Add Row
          </button>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-white/5 p-5">
              <p className="text-gray-400">Total Debit</p>

              <h2 className="text-2xl text-emerald-400">
                ₹{totalDebit.toLocaleString("en-IN")}
              </h2>
            </div>

            <div className="rounded-xl bg-white/5 p-5">
              <p className="text-gray-400">Total Credit</p>

              <h2 className="text-2xl text-indigo-400">
                ₹{totalCredit.toLocaleString("en-IN")}
              </h2>
            </div>
          </div>

          {totalDebit !== totalCredit && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-red-400">
              Debit and Credit must be equal.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-white/10 px-8 py-5">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl border border-white/10"
          >
            Cancel
          </button>

          <button
            disabled={totalDebit !== totalCredit}
            onClick={saveJournal}
            className="px-8 py-3 rounded-xl bg-amber-400 text-black disabled:opacity-50"
          >
            {editing ? "Update Journal" : "Post Journal"}
          </button>
        </div>
      </div>
    </div>
  );
}
